import {SlidersIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, Tooltip} from '@primer/react'
import {clsx} from 'clsx'
import {useState} from 'react'

import {
  getReasoningEffortLabel,
  REASONING_EFFORT_OPTIONS,
  type ReasoningEffort,
} from '@github-ui/copilot-shared-ui/utils/reasoning-effort'
import styles from './ReasoningEffortPicker.module.css'

interface ReasoningEffortPickerProps {
  selectedReasoningEffort?: ReasoningEffort
  onUpdateReasoningEffort: (reasoningEffort: ReasoningEffort | undefined) => void
  // The reasoning efforts the selected model supports
  supportedReasoningEfforts: readonly string[] | undefined
  // Collapse the trigger to just the icon when horizontal space is tight.
  iconOnly?: boolean
  /**
   * Opts the trigger into the composer's container-query compaction. The
   * accessible name already carries the level, so collapsing the visible label
   * does not take it with it.
   */
  responsiveIconOnly?: boolean
  triggerClassName?: string
  disabled?: boolean
}

export function ReasoningEffortPicker({
  selectedReasoningEffort,
  onUpdateReasoningEffort,
  supportedReasoningEfforts,
  iconOnly = false,
  responsiveIconOnly = false,
  triggerClassName,
  disabled = false,
}: ReasoningEffortPickerProps) {
  // Whether the container query has actually collapsed the trigger. Measured
  // from the rendered width because the decision is CSS's, so the tooltip can
  // name the level exactly when the visible label is gone.
  const [compact, setCompact] = useState(false)
  const updateTooltip = (target: HTMLElement) => {
    setCompact(current => {
      const next = target.getBoundingClientRect().width <= 32
      return current === next ? current : next
    })
  }
  // Hide until we know the model supports reasoning
  if (!supportedReasoningEfforts || supportedReasoningEfforts.length === 0) {
    return null
  }

  const options = REASONING_EFFORT_OPTIONS.filter(option => supportedReasoningEfforts.includes(option.value))

  // No explicit selection is "Default": send no override and let the backend pick the
  // model's own default effort (model-specific, not always medium).
  const triggerLabel = `Reasoning level: ${getReasoningEffortLabel(selectedReasoningEffort)}`

  const textButton = (
    <ActionMenu.Button
      className={clsx(styles.menuButton, triggerClassName)}
      variant="invisible"
      size="medium"
      aria-label={triggerLabel}
      disabled={disabled}
      data-responsive-effort-control={responsiveIconOnly ? '' : undefined}
      onPointerOverCapture={event => updateTooltip(event.currentTarget)}
      onFocusCapture={event => updateTooltip(event.currentTarget)}
    >
      {responsiveIconOnly ? (
        <span className={styles.responsiveIcon} data-responsive-effort-icon aria-hidden="true">
          <SlidersIcon />
        </span>
      ) : null}
      <span className={styles.buttonName} data-responsive-effort-label={responsiveIconOnly ? '' : undefined}>
        {getReasoningEffortLabel(selectedReasoningEffort)}
      </span>
    </ActionMenu.Button>
  )

  return (
    <ActionMenu>
      {iconOnly ? (
        // Icon-only (tight layouts, e.g. the global chat overlay): a square IconButton with
        // no dropdown caret or label padding. Its aria-label supplies both the accessible
        // name and the hover tooltip ("Reasoning level: …").
        <ActionMenu.Anchor>
          <IconButton
            icon={SlidersIcon}
            aria-label={triggerLabel}
            variant="invisible"
            size="medium"
            disabled={disabled}
          />
        </ActionMenu.Anchor>
      ) : responsiveIconOnly && !disabled ? (
        // A host that opted into compaction gets a tooltip, because once the
        // container query hides the label the trigger is an unlabelled icon.
        // Primer's Tooltip has to wrap `ActionMenu.Button` directly — ActionMenu
        // matches on that identity to thread its anchor props — so this is
        // inline rather than a helper component. It is skipped while disabled,
        // where TooltipV2 throws on a native disabled button.
        <Tooltip text={compact ? triggerLabel : 'Reasoning level'}>{textButton}</Tooltip>
      ) : (
        textButton
      )}
      <ActionMenu.Overlay width="small" align="end">
        <ActionList selectionVariant="single">
          <div className={styles.menuHeading}>Reasoning level</div>
          <ActionList.Item
            selected={selectedReasoningEffort === undefined}
            onSelect={() => onUpdateReasoningEffort(undefined)}
          >
            Default
            <ActionList.Description variant="block">
              Let the model pick its default reasoning effort
            </ActionList.Description>
          </ActionList.Item>
          {options.map(option => (
            <ActionList.Item
              key={option.value}
              selected={option.value === selectedReasoningEffort}
              onSelect={() => onUpdateReasoningEffort(option.value)}
            >
              {option.label}
              <ActionList.Description variant="block">{option.description}</ActionList.Description>
            </ActionList.Item>
          ))}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
